<script setup lang="ts">
import { computed, nextTick, ref, useSlots, useTemplateRef, watch } from 'vue';
import type { GenericSetInterface } from '@Types';
import type { PageState } from 'primevue/paginator';
import { getShelterCurrency } from '@Helpers';

const emit = defineEmits<{
  onRowReorder: [rows: any[]];
}>();

const props = withDefaults(
  defineProps<{
    columns: (string | null)[];
    entityId?: string;
    hasSelectable?: boolean;
    isRowLocked?: (row: any) => boolean;
    reorderable?: boolean;
    rows: any[];
    rowsPerPage?: number;
    subtitleStyle?: string[];
    tableHeight?: string;
    tableSubtitle?: string;
    usePagination?: boolean;
  }>(),
  {
    rowsPerPage: 50,
  }
);

const tableContainerRef = useTemplateRef<HTMLElement | null>('tableContainerRef');
const firstRow = useTemplateRef<HTMLElement | null | HTMLElement[]>('firstRow');
const shelterCurrency = getShelterCurrency();

const dynamicSelectedRows = defineModel<GenericSetInterface>('dynamicSelectedRows', {
  default: () => ({}),
});

const currentPage = ref(1);
/** The row whose handle is pressed: only that row is `draggable`, so text and controls elsewhere still work. */
const armedRowIndex = ref<number | null>(null);
const draggedRowIndex = ref<number | null>(null);
const dropTargetIndex = ref<number | null>(null);

const slots = useSlots();

const activeColumns = computed(() => props.columns.filter((col): col is string => Boolean(col)));
const rowDetailsColspan = computed(
  () => activeColumns.value.length + Number(!!props.hasSelectable) + Number(!!props.reorderable)
);
const firstPage = computed(() => (currentPage.value - 1) * props.rowsPerPage);
const showPaginator = computed(() => props.usePagination && props.rows.length > props.rowsPerPage);

const paginatedRows = computed(() => {
  if (!props.usePagination) return props.rows;
  const start = (currentPage.value - 1) * props.rowsPerPage;
  const end = start + props.rowsPerPage;
  return props.rows.slice(start, end);
});

const allRowsSelected = computed({
  get: () => {
    if (!props.entityId) return false;

    const selectedSet = dynamicSelectedRows.value[props.entityId] || new Set();

    return props.rows.length > 0 && selectedSet.size === props.rows.length;
  },
  set: (value: boolean) => {
    if (!props.entityId) return;

    if (!value) {
      dynamicSelectedRows.value[props.entityId] = new Set();
      return;
    }

    const ids = props.rows.map((row) => row.id);
    dynamicSelectedRows.value[props.entityId] = new Set(ids);
  },
});

function isRowSelected(id: any) {
  if (!props.entityId) return false;

  const selectedSet = dynamicSelectedRows.value[props.entityId];

  return selectedSet ? selectedSet.has(id) : false;
}

function onSelectRow({ id }: any) {
  if (!props.entityId) return;

  if (!dynamicSelectedRows.value[props.entityId]) {
    dynamicSelectedRows.value[props.entityId] = new Set();
  }

  const set = dynamicSelectedRows.value[props.entityId];

  if (set.has(id)) return set.delete(id);

  set.add(id);
}

watch(
  () => props.rows,
  () => clearValues()
);

function clearValues() {
  if (!props.entityId) return;

  dynamicSelectedRows.value[props.entityId] = new Set();

  nextTick(() => tableContainerRef?.value?.scrollTo({ top: 0 }));
}

function rowDragListeners(rowIndex: number) {
  if (!props.reorderable) return {};

  return {
    dragend: onRowDragEnd,
    dragover: (event: DragEvent) => onRowDragOver(event, rowIndex),
    dragstart: (event: DragEvent) => onRowDragStart(event, rowIndex),
    drop: (event: DragEvent) => onRowDrop(event, rowIndex),
  };
}

/** `!` because the table's `divide-y` row borders outrank a plain utility. */
function dropIndicatorClass(rowIndex: number) {
  const isTarget = dropTargetIndex.value === rowIndex && draggedRowIndex.value !== rowIndex;

  if (!props.reorderable || !isTarget || draggedRowIndex.value === null) return '';

  return draggedRowIndex.value < rowIndex ? '!border-b-2 !border-b-brand-500' : '!border-t-2 !border-t-brand-500';
}

function onHandleMouseDown(row: any, rowIndex: number) {
  if (props.isRowLocked?.(row)) return;

  armedRowIndex.value = rowIndex;
}

function onRowDragStart(event: DragEvent, rowIndex: number) {
  draggedRowIndex.value = rowIndex;

  // NOTE :: Firefox only starts a drag when the transfer carries data.
  event.dataTransfer?.setData('text/plain', String(rowIndex));
}

function onRowDragOver(event: DragEvent, rowIndex: number) {
  if (draggedRowIndex.value === null) return;

  event.preventDefault();
  dropTargetIndex.value = rowIndex;
}

/** Indexes are on the shown page; the emitted list is the whole `rows` list, reordered. */
function onRowDrop(event: DragEvent, rowIndex: number) {
  event.preventDefault();

  const fromIndex = draggedRowIndex.value;

  onRowDragEnd();

  if (fromIndex === null || fromIndex === rowIndex) return;

  const reordered = [...props.rows];
  const [moved] = reordered.splice(firstPage.value + fromIndex, 1);

  reordered.splice(firstPage.value + rowIndex, 0, moved);

  emit('onRowReorder', reordered);
}

function onRowDragEnd() {
  armedRowIndex.value = null;
  draggedRowIndex.value = null;
  dropTargetIndex.value = null;
}

function sanitizeFlaggedColumns(col: string) {
  return col.split('__')[0];
}

function headerLines(column: string) {
  return sanitizeFlaggedColumns(column).split('\n');
}

function onPageChange(state: PageState) {
  currentPage.value = state.page + 1;

  nextTick(() => scrollTableToTop());
}

function scrollTableToTop() {
  const row = Array.isArray(firstRow.value) ? firstRow.value[0] : firstRow.value;
  const container = tableContainerRef.value;

  if (row && container) {
    container.scrollTo({
      top: row.offsetTop - container.offsetTop,
      behavior: 'smooth',
    });
  }
}
</script>

<template>
  <div class="border-none">
    <p
      v-if="tableSubtitle"
      :class="[subtitleStyle || 'mb-4 text-sm font-medium']"
    >
      {{ tableSubtitle }}
    </p>

    <div class="overflow-x-auto rounded-md border border-gray-200">
      <div
        ref="tableContainerRef"
        :class="['overflow-y-auto', tableHeight || 'max-h-[380px]']"
      >
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="sticky top-0 z-10 border-b-2 bg-slate-100">
            <tr>
              <th
                v-if="reorderable"
                class="p-3 text-left text-sm font-semibold text-gray-800"
              >
                #
              </th>

              <th
                v-if="hasSelectable"
                class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                <Checkbox
                  v-model="allRowsSelected"
                  v-tooltip.top="$t('common.selectAll')"
                  binary
                  :inputId="`${entityId}-select-all`"
                />
              </th>

              <th
                v-for="(col, index) in activeColumns"
                class="p-3 text-left text-sm font-semibold text-gray-800"
                :key="index"
              >
                <span
                  v-for="(line, lineIndex) in headerLines(col)"
                  :key="lineIndex"
                  :class="{ 'block whitespace-nowrap': headerLines(col).length > 1 }"
                >
                  {{ line }}
                </span>
              </th>
            </tr>
          </thead>

          <tbody class="divide-y divide-gray-200 bg-white">
            <template
              v-for="(row, rowIndex) in paginatedRows"
              :key="row.id"
            >
              <tr
                :ref="rowIndex === 0 ? 'firstRow' : undefined"
                :class="[
                  {
                    'bg-gray-50': rowIndex % 2 !== 0,
                    'opacity-50': reorderable && draggedRowIndex === rowIndex,
                  },
                  dropIndicatorClass(rowIndex),
                ]"
                :draggable="reorderable && armedRowIndex === rowIndex ? 'true' : undefined"
                v-on="rowDragListeners(rowIndex)"
              >
                <td
                  v-if="reorderable"
                  class="whitespace-nowrap border-r px-3 py-2 text-sm text-gray-800"
                >
                  <div
                    :class="['flex items-center gap-1.5', { 'cursor-move': !isRowLocked?.(row) }]"
                    @mousedown="onHandleMouseDown(row, rowIndex)"
                    @mouseup="armedRowIndex = null"
                  >
                    <slot
                      name="reorderHandle"
                      :row
                      :rowIndex
                    >
                      <i class="pi pi-bars text-gray-400" />
                    </slot>
                  </div>
                </td>

                <td
                  v-if="hasSelectable"
                  class="whitespace-nowrap border-r px-3 py-2 text-sm text-gray-500"
                >
                  <Checkbox
                    binary
                    :inputId="row.id"
                    :modelValue="isRowSelected(row.id)"
                    @change="() => onSelectRow(row)"
                  />
                </td>

                <td
                  v-for="(col, colIndex) in activeColumns"
                  class="whitespace-nowrap border-r px-3 py-2 text-sm text-gray-800 last:border-r-0"
                  :key="colIndex"
                >
                  <span
                    v-if="
                      typeof row[col] === 'string' && row[col].startsWith('-') && row[col].includes(shelterCurrency)
                    "
                    class="text-red-500"
                  >
                    {{ shelterCurrency }}({{ row[col].replace(`-${shelterCurrency}`, '') }})
                  </span>

                  <span
                    v-else-if="typeof row[col] === 'string' && row[col].startsWith('$(') && row[col].endsWith(')')"
                    class="text-red-500"
                  >
                    {{ shelterCurrency }}({{ row[col].slice(2, -1) }})
                  </span>

                  <slot
                    v-else
                    :colIndex
                    :column="col"
                    :row
                    :rowIndex
                  >
                    {{ row[col] }}
                  </slot>
                </td>
              </tr>

              <tr
                v-if="slots.rowDetails"
                :class="{ 'bg-gray-50': rowIndex % 2 !== 0 }"
              >
                <td
                  class="p-0"
                  :colspan="rowDetailsColspan"
                >
                  <slot
                    name="rowDetails"
                    :row
                    :rowIndex
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <div
      v-if="showPaginator"
      class="mt-4 flex justify-center"
    >
      <Paginator
        :first="firstPage"
        :rows="rowsPerPage"
        :totalRecords="rows.length"
        @page="onPageChange"
      />
    </div>
  </div>
</template>
